import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useLanguage } from '../components/Navbar';
import SEO from '../components/SEO';

const ScheduleSection = styled.section`
  padding: 120px 0 80px;
  background-color: white;
  color: black;
`;

const ScheduleContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ScheduleHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const ScheduleTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  color: black;
  
  &:after {
    display: none;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ScheduleSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  color: #333;
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ScheduleForm = styled(motion.form)`
  background-color: white;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid #eaeaea;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: black;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const FormButton = styled(motion.button)`
  background-color: #000;
  color: white;
  border: none;
  padding: 15px 30px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #333;
  }
`;

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const InfoItem = styled(motion.div)`
  margin-bottom: 30px;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  position: relative;
  display: inline-block;
  color: black;
  
  &:after {
    display: none;
  }
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 10px;
  color: #333;
`;

const AvailabilityCalendar = styled.div`
  margin-top: 20px;
  border: 1px solid #e1e1e1;
  border-radius: 10px;
  padding: 20px;
  background-color: #f9f9f9;
`;

const CalendarDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
  margin-top: 10px;
`;

const DayHeader = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: 0.8rem;
  color: #000;
  padding: 5px 0;
`;

const DayCell = styled.div<{ available?: boolean; isToday?: boolean }>`
  height: 35px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  cursor: ${props => props.available ? 'pointer' : 'default'};
  background-color: ${props => 
    props.isToday 
      ? '#000' 
      : props.available 
        ? 'white' 
        : '#f2f2f2'};
  color: ${props => 
    props.isToday 
      ? 'white' 
      : props.available 
        ? 'black' 
        : '#999'};
  border: 1px solid ${props => 
    props.isToday 
      ? '#000' 
      : props.available 
        ? '#e1e1e1' 
        : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    transform: ${props => props.available ? 'scale(1.05)' : 'none'};
    background-color: ${props => props.available && !props.isToday ? '#f5f5f5' : null};
  }
`;

const InquiryTypes = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InquiryType = styled(motion.div)`
  padding: 20px;
  border-radius: 10px;
  background-color: white;
  border: 1px solid #e1e1e1;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    border-color: #000;
  }
  
  h4 {
    font-size: 1.1rem;
    margin-bottom: 10px;
    font-weight: 600;
  }
  
  p {
    font-size: 0.9rem;
    color: #555;
  }
`;

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const Schedule: React.FC = () => {
  const { language } = useLanguage();
  
  // SEO metadata for Schedule page
  const scheduleSeoData = {
    en: {
      title: 'Schedule Meeting - Barkin Sayin | Book an Appointment',
      description: 'Schedule a meeting with Barkin Sayin to discuss business opportunities, investment partnerships, or consulting services in fintech and smart TV technology.',
      keywords: [
        'book meeting Barkin Sayin',
        'schedule appointment',
        'fintech consultation',
        'business meeting',
        'investment discussion',
        'entrepreneur mentoring',
        'digital transformation consulting'
      ]
    },
    tr: {
      title: 'Toplantı Planla - Barkın Sayın | Randevu Al',
      description: 'Fintech ve akıllı TV teknolojisi alanlarında iş fırsatları, yatırım ortaklıkları veya danışmanlık hizmetlerini görüşmek için Barkın Sayın ile toplantı planlayın.',
      keywords: [
        'Barkın Sayın toplantı planla',
        'randevu al',
        'fintech danışmanlık',
        'iş toplantısı',
        'yatırım görüşmesi',
        'girişimci mentorluğu',
        'dijital dönüşüm danışmanlığı'
      ]
    }
  };
  
  const seoTitle = scheduleSeoData[language as keyof typeof scheduleSeoData]?.title || scheduleSeoData.en.title;
  const seoDescription = scheduleSeoData[language as keyof typeof scheduleSeoData]?.description || scheduleSeoData.en.description;
  const seoKeywords = scheduleSeoData[language as keyof typeof scheduleSeoData]?.keywords || scheduleSeoData.en.keywords;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    meetingType: '',
    notes: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Schedule data:', formData);
    // Here you would normally send the form data to your backend
    alert('Thank you for scheduling a meeting! I will confirm the details soon.');
    setFormData({ name: '', email: '', phone: '', date: '', time: '', meetingType: '', notes: '' });
  };

  // Mock data for available days
  const currentDate = new Date();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Generate dates for the current month
  const generateCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Get the day of the week for the first day of the month
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<DayCell key={`empty-${i}`} />);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = date.getDate() === currentDate.getDate() && 
                     date.getMonth() === currentDate.getMonth() && 
                     date.getFullYear() === currentDate.getFullYear();
                     
      // Mock available days (weekdays that are not in the past)
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isAvailable = !isPast && !isWeekend;
      
      days.push(
        <DayCell 
          key={`day-${day}`} 
          available={isAvailable}
          isToday={isToday}
          onClick={() => isAvailable && handleDateSelect(date)}
        >
          {day}
        </DayCell>
      );
    }
    
    return days;
  };
  
  const handleDateSelect = (date: Date) => {
    const formattedDate = date.toISOString().split('T')[0];
    setFormData(prev => ({ ...prev, date: formattedDate }));
  };

  return (
    <ScheduleContainer>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScheduleSection>
          <ScheduleContainer>
            <ScheduleHeader>
              <ScheduleTitle
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Schedule a Meeting
              </ScheduleTitle>
              
              <ScheduleSubtitle
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Book a time slot for a consultation, collaboration discussion, or just a friendly conversation about your ideas and projects.
              </ScheduleSubtitle>
            </ScheduleHeader>
            
            <ScheduleGrid>
              <ScheduleForm
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                onSubmit={handleSubmit}
              >
                <FormGroup>
                  <FormLabel htmlFor="name">Your Name</FormLabel>
                  <FormInput 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleChange}
                    required 
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="email">Your Email</FormLabel>
                  <FormInput 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    required 
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="phone">Phone Number (optional)</FormLabel>
                  <FormInput 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="date">Preferred Date</FormLabel>
                  <FormInput 
                    type="date" 
                    id="date" 
                    name="date" 
                    value={formData.date}
                    onChange={handleChange}
                    required 
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="time">Preferred Time</FormLabel>
                  <FormSelect
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a time</option>
                    <option value="09:00">9:00 AM</option>
                    <option value="10:00">10:00 AM</option>
                    <option value="11:00">11:00 AM</option>
                    <option value="13:00">1:00 PM</option>
                    <option value="14:00">2:00 PM</option>
                    <option value="15:00">3:00 PM</option>
                    <option value="16:00">4:00 PM</option>
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="meetingType">Meeting Type</FormLabel>
                  <FormSelect
                    id="meetingType"
                    name="meetingType"
                    value={formData.meetingType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select meeting type</option>
                    <option value="consultation">Business Consultation</option>
                    <option value="project">Project Discussion</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="speaking">Speaking Engagement</option>
                    <option value="other">Other</option>
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="notes">Additional Notes</FormLabel>
                  <FormTextarea 
                    id="notes" 
                    name="notes" 
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Please provide any additional information about the meeting topic..."
                  />
                </FormGroup>
                
                <FormButton
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Request Meeting
                </FormButton>
              </ScheduleForm>
              
              <InfoPanel>
                <InfoItem
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <InfoTitle>Meeting Availability</InfoTitle>
                  <InfoText>Check out my calendar for available time slots. I typically meet Monday through Friday, 9AM to 5PM CET.</InfoText>
                  
                  <AvailabilityCalendar>
                    <strong>May 2023</strong>
                    <CalendarDays>
                      {dayNames.map(day => (
                        <DayHeader key={day}>{day}</DayHeader>
                      ))}
                      {generateCalendarDays()}
                    </CalendarDays>
                  </AvailabilityCalendar>
                </InfoItem>
                
                <InfoItem
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <InfoTitle>Meeting Types</InfoTitle>
                  <InfoText>Choose the meeting format that works best for your needs:</InfoText>
                  
                  <InquiryTypes>
                    <InquiryType
                      whileHover={{ y: -5 }}
                    >
                      <h4>Virtual Meeting</h4>
                      <p>Zoom or Google Meet call at your convenience</p>
                    </InquiryType>
                    
                    <InquiryType
                      whileHover={{ y: -5 }}
                    >
                      <h4>In-Person</h4>
                      <p>Face-to-face meeting in Berlin or Hamburg</p>
                    </InquiryType>
                    
                    <InquiryType
                      whileHover={{ y: -5 }}
                    >
                      <h4>Phone Call</h4>
                      <p>Quick discussion via phone or WhatsApp</p>
                    </InquiryType>
                  </InquiryTypes>
                </InfoItem>
                
                <InfoItem
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <InfoTitle>Need Urgent Help?</InfoTitle>
                  <InfoText>For time-sensitive matters, please email me directly at:</InfoText>
                  <InfoText><strong>meetings@barkinsayin.com</strong></InfoText>
                </InfoItem>
              </InfoPanel>
            </ScheduleGrid>
          </ScheduleContainer>
        </ScheduleSection>
      </motion.div>
    </ScheduleContainer>
  );
};

export default Schedule; 